import FootballStatsCategory from "../../../../sports/football/stats/football-stats-category";
import FootballStringifier from "../../../stringifiers/football-stringifier";

export const ScoringSerializer = {
    serialize(driveStats, plays, awayPlayers, homePlayers, gameContext) {
        return driveStats
            .reduce((acc, { drive, stats }) => {
                const lastPlay = drive.plays[drive.plays.length - 1];
                if (lastPlay) {
                    const scoreStats = stats
                        .filter(
                            (stat) =>
                                stat.values?.rouge ||
                                (stat.category ===
                                    FootballStatsCategory.FIELD_GOAL &&
                                    stat.values?.completed) ||
                                (stat.category ===
                                    FootballStatsCategory.SAFETY &&
                                    stat.values?.completed) ||
                                (stat.category ===
                                    FootballStatsCategory.DEUCE &&
                                    stat.values?.completed)
                        )
                        .map((s) => ({ scoreStat: s }));
                    // A single touchdown play can produce multiple stat rows
                    // with values.touchdowns set (e.g. FUMBLE_RECOVERED for
                    // the recoverer plus RUSH for the rusher on a fumble-TD
                    // play). Dedupe by playId so we emit one TD entry per
                    // scoring play. If two different scoring plays end up on
                    // the same drive bucket (drive-attribution misroute),
                    // each gets its own entry.
                    const tdStatsByPlay = new Map();
                    stats
                        .filter((stat) => stat.values?.touchdowns)
                        .forEach((stat) => {
                            if (!tdStatsByPlay.has(stat.playId)) {
                                tdStatsByPlay.set(stat.playId, stat);
                            }
                        });
                    tdStatsByPlay.forEach((tdStat) => {
                        const patStat = stats.find(
                            (stat) =>
                                [
                                    FootballStatsCategory.PAT_RUN,
                                    FootballStatsCategory.PAT_KICK,
                                    FootballStatsCategory.PAT_PASS
                                ].includes(stat.category) &&
                                stat.teamId === tdStat.teamId
                        );
                        scoreStats.push({ scoreStat: tdStat, patStat });
                    });
                    // True defensive PATs (PatDefenseCompleteStats) write
                    // playerId=null. An offensive PAT_COMPLETE sets playerId
                    // to the kicker — those are tied to their own team's TD,
                    // not a defensive return, so we ignore them here even if
                    // they have been misrouted onto a drive whose tdStat is
                    // the other team's TD.
                    const defensePatStat = stats.find(
                        (stat) =>
                            stat.category ===
                                FootballStatsCategory.PAT_COMPLETE &&
                            stat.playerId == null
                    );
                    const tdTeamIds = new Set(
                        Array.from(tdStatsByPlay.values()).map(
                            (td) => td.teamId
                        )
                    );
                    if (
                        defensePatStat &&
                        !tdTeamIds.has(defensePatStat.teamId)
                    ) {
                        scoreStats.push({ scoreStat: defensePatStat });
                    }
                    //
                    const { contextSnapshot } =
                        plays.filter((p) => p.index > lastPlay.index)?.[0] ||
                        {};

                    scoreStats.forEach((s) => {
                        // A stat can be bucketed onto a drive whose `plays`
                        // don't contain its play (PAT stats resolve to the TD
                        // drive by design; drifted links on corrupt games), so
                        // fall back to the full play list. A stat whose play
                        // is gone entirely can't be serialized — skip it.
                        const scoringPlay =
                            drive.plays.find(
                                (p) => p.id === s.scoreStat.playId
                            ) || plays.find((p) => p.id === s.scoreStat.playId);
                        if (!scoringPlay) {
                            return;
                        }
                        acc.push({
                            ...ScoringSerializer._serialize(
                                drive,
                                s,
                                stats,
                                [...awayPlayers, ...homePlayers],
                                { ...gameContext, ...contextSnapshot },
                                plays,
                                scoringPlay
                            ),
                            playIndex: scoringPlay.index
                        });
                    });
                }

                return acc;
            }, [])
            .sort((a, b) => a.playIndex - b.playIndex);
    },

    _serialize(
        drive,
        { scoreStat, patStat },
        stats,
        players,
        context,
        allPlays = [],
        scoringPlay = null
    ) {
        const { startPeriod, index, plays } = drive;
        const { score, homeTeamId } = context;

        scoringPlay =
            scoringPlay ||
            plays.find((p) => p.id === scoreStat.playId) ||
            allPlays.find((p) => p.id === scoreStat.playId);
        const { time: scoreTime, contextSnapshot: scorePlayContext } =
            scoringPlay;

        const scoreMMSS = FootballStringifier.milisecondsToMMSS(scoreTime);

        const { type, yards, scoringUniform, isDropKick } = _getScoreProps(
            scoreStat,
            stats,
            scorePlayContext,
            context.gameConfig
        );
        const { patBy, patCode, patPoints } = _getPatProps(patStat, players);
        const scoringPlayer = players.find((p) => p.id === scoreStat.playerId);
        const homeScored = scoreStat.teamId === homeTeamId;

        // Get score from the play AFTER this scoring play (not after the drive)
        const nextPlay = allPlays
            .filter((p) => p.index > scoringPlay.index)
            .sort((a, b) => a.index - b.index)[0];
        const scoreAfterPlay = nextPlay?.contextSnapshot?.score || score;

        return {
            secs: parseInt(scoreMMSS.split(":")[1], 10),
            mins: parseInt(scoreMMSS.split(":")[0], 10),
            homeTeam: homeScored,
            homeScore: scoreAfterPlay[0],
            visitorScore: scoreAfterPlay[1],
            quarter: startPeriod,
            driveIdx: index,
            how: scoreStat.category,
            type,
            yards,
            passer: null, // TODO
            scorer: scoringUniform || scoringPlayer?.uniform,
            patBy,
            patCode,
            patPoints,
            isDropKick
        };
    }
};

const _getScoreProps = (stat, stats, scorePlayContext, gameConfig) => {
    const { category, values, playId } = stat;
    const {
        rouge,
        touchdowns,
        completed,
        yards: statYards,
        dropKick
    } = values || {};

    let yards = 0;
    let type = "";
    let scoringUniform = "";
    let isDropKick = false;

    if (rouge) {
        yards = statYards;
        type = "1PT";
    } else if (touchdowns) {
        yards =
            statYards ||
            stats.find(
                (s) =>
                    s.playId === playId &&
                    s.category === category &&
                    s.values?.yards
            )?.values?.yards ||
            0;
        type = "TD";
    } else if (category === FootballStatsCategory.FIELD_GOAL && completed) {
        type = dropKick ? "DKFG" : "FG";
        yards = statYards;
        isDropKick = !!dropKick;
    } else if (category === FootballStatsCategory.PAT_COMPLETE) {
        const { homeTeamHasPossession, fieldPosition } = scorePlayContext;
        const { fieldLength } = gameConfig.general;
        yards = homeTeamHasPossession
            ? fieldPosition
            : fieldLength - fieldPosition;
        type = "PAT";
    } else if (category === FootballStatsCategory.SAFETY && completed) {
        type = "SAF";
        scoringUniform = "TM";
    } else if (category === FootballStatsCategory.DEUCE && completed) {
        type = "DEUCE";
        yards = statYards;
    }

    return { yards, type, scoringUniform, isDropKick };
};

const _getPatProps = (patStat, players) => {
    let patBy, patCode, patPoints;
    if (patStat) {
        const { category, values, playerId, code } = patStat;
        const { completed, code: altCode, dropKick } = values;
        patBy = players.find((p) => p.id === playerId)?.uniform;
        patPoints = code || altCode;
        switch (category) {
            case FootballStatsCategory.PAT_RUN:
                patCode = completed ? 17 : 33;
                break;
            case FootballStatsCategory.PAT_PASS:
                patCode = completed ? 18 : 34;
                break;
            case FootballStatsCategory.PAT_KICK:
                // Drop kick uses bit 3 (value 8): 24 = good, 40 = fail
                if (dropKick) {
                    patCode = completed ? 24 : 40;
                } else {
                    patCode = completed ? 16 : 32;
                }
                break;
            default:
                patCode = 0;
        }
    }
    return { patBy, patCode, patPoints };
};
